import { z } from "zod";
import { IanaTimezoneSchema } from "./timezone.schema";

const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format");

const FUTURE_DATE_TOLERANCE_DAYS = 1;

function maxAllowedDate(): string {
  const limit = new Date();
  limit.setUTCDate(limit.getUTCDate() + FUTURE_DATE_TOLERANCE_DAYS);
  return limit.toISOString().slice(0, 10);
}

export const StrangerFileStatusSchema = z.enum(["unknown", "pending", "resolved", "untrained"]);

export const StrangerFilesListQuerySchema = z
  .strictObject({
    dateFrom: IsoDateSchema,
    dateTo: IsoDateSchema,
    search: z.string().max(100).optional(),
    status: z.enum(["all", "unknown", "pending", "resolved", "untrained"]).optional(),
    consumer: z.string().max(100).optional(),
    sortOrder: z.enum(["asc", "desc"]).optional().default("desc"),
    page: z.coerce.number().int().min(1).optional().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).optional().default(30),
    timezone: IanaTimezoneSchema,
  })
  .refine((value) => value.dateFrom <= value.dateTo, {
    message: "dateFrom must not be after dateTo",
    path: ["dateFrom"],
  })
  .refine((value) => value.dateTo <= maxAllowedDate(), {
    message: "dateTo must not be in the future",
    path: ["dateTo"],
  });

export const StrangerFilesListItemSchema = z.object({
  requestId: z.string().uuid().nullable(),
  fileName: z.string().nullable(),
  fileSize: z.coerce.number().nullable().optional(),
  thumbnailUrl: z.string().nullable().optional(),
  fileUrl: z.string().nullable().optional(),
  status: StrangerFileStatusSchema,
  consumerName: z.string(),
  classificationResult: z.unknown().nullable(),
  submittedAt: z.string(),
});

export const StrangerFilesListDataSchema = z.object({
  rows: z.array(StrangerFilesListItemSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});

export type StrangerFilesListQuery = z.infer<typeof StrangerFilesListQuerySchema>;
export type StrangerFilesListItem = z.infer<typeof StrangerFilesListItemSchema>;
export type StrangerFilesListData = z.infer<typeof StrangerFilesListDataSchema>;
export type StrangerFileStatus = z.infer<typeof StrangerFileStatusSchema>;
